/**
 * Legal / agreement copy + per-section consent config for the Sacrifice
 * "Agreements" gate.
 *
 * DRAFT copy — generated from docs/legal/agreements-draft.md, adapted from the
 * ProveX escrow legal text for the token-distribution campaign. REQUIRES HUMAN
 * LEGAL REVIEW before launch. Editing any section's text changes its content
 * hash, which re-prompts users to re-accept ONLY that section (see
 * sacrifice-utils/consent).
 *
 * The three sections defined here (disclaimer / terms / privacy) are GLOBAL —
 * identical across every campaign and accepted once (the hash-versioned consent
 * persists, so on a later campaign they show as already agreed). The Agreements
 * modal's 4th section is the PER-CAMPAIGN sacrifice disclaimer, accepted by
 * signing in (EIP-712 binds termsOfUse = projectDisclaimerShort); it is NOT
 * tracked here.
 *
 * Do not edit by hand casually — prefer editing docs/legal/agreements-draft.md
 * and regenerating, so the markdown stays the reviewable source of truth.
 */
import { hashString } from 'sacrifice-utils/consent'

export const DISCLAIMER_TEXT = `TRUSTLESS AND PERMISSIONLESS SYSTEM

This is a trustless, permissionless system. There is no guaranteed customer support. A best-effort channel is available via Simplex private messaging. There is no undo button. There is no refund. There is no recourse if something goes wrong. A sacrifice is a one-way, irreversible deposit — funds you sacrifice are NOT returned, under any circumstances. Smart contracts and on-chain transactions execute exactly as written and broadcast — if there is a bug, or if you make a mistake, your funds may be lost permanently. There is no one to appeal to.

ERRORS WILL RESULT IN LOSS OF FUNDS

Any error you encounter — whether caused by your mistake, a software bug, network issues, or protocol vulnerabilities — may result in immediate and permanent loss of your cryptocurrency. Once a transaction is confirmed on the blockchain, it CANNOT be reversed, regardless of the circumstances. There is no insurance. There is no protection. There is no recovery mechanism. A sacrifice is intentionally permanent: it is a deliberate, irreversible deposit made to earn points, not a deposit you can later withdraw.

You are interacting directly with public blockchains and the deposit addresses generated for you. If you send funds to the wrong address, they are gone. If you approve a malicious transaction, your funds are gone. On the blockchain it is very easy to make mistakes, and you must be very careful. The following common mistakes will cost you funds, points, or both, with no recourse:

- Sending cryptocurrency directly to a smart contract address (on Ethereum or any similar smart-contract-enabled blockchain) instead of to your generated deposit address. This nearly always results in the total loss of your funds.
- Sacrificing a memo-based or destination-tag-based cryptocurrency — such as XRP — without including the exact memo or destination tag you were instructed to include. If you omit it, you get no points, and your funds may be unrecoverable.
- Sending a token, coin, or network that is not supported. If you send something unsupported, you get no points, and your funds may be unrecoverable.
- Sacrificing before or after the published sacrifice-phase period. If you send outside the phase window, you get no points.

BETA STATUS RISKS

This application has NOT been battle-tested in production. Critical bugs may exist. The user interface may display incorrect information, including incorrect United States dollar valuations and incorrect point totals. Indexing logic may miss, double-count, or misprice a sacrifice. Security vulnerabilities may be present. By using this beta software, you accept the very real possibility that you could lose all funds you sacrifice through this platform, and that the points credited to you could be wrong, delayed, or revised.

SMART CONTRACT AND PROTOCOL RISKS

Blockchains, smart contracts, and cryptocurrencies are all cutting-edge technologies, and as such there is a risk, however small, of total failure. Software is hard. Computers are hard. Distributed software on distributed computers is harder. It is a miracle this stuff works at all. Strong cryptography seems unlikely to be broken, but if it is, everything will probably be broken. If you can, learn to code; or have the smartest coder or computer scientist you can find read over any code you plan to execute before you execute it.

Any smart contracts involved in this system are immutable. They cannot be paused, upgraded, or modified to fix bugs or recover funds. The pricing and indexing systems that observe your deposits across many blockchains may fail, lag, or produce incorrect results. Blockchain data sources, indexers, and exchange price feeds are third-party dependencies that may experience downtime or return wrong data. If these systems fail, your sacrifice may be priced incorrectly, credited late, or not credited at all, with no recourse.

The displayed United States dollar pricing and points are best-effort estimates produced from third-party price sources and may be wrong. Points are awarded according to a published time-decay schedule; the rate, bonuses, and resulting point totals are determined by the published rules and the system's interpretation of on-chain data, and may be recalculated.

MEV and front-running: Your transactions are visible in public mempools before confirmation. Sophisticated actors may front-run, sandwich, or otherwise extract value from your transactions. This is an inherent characteristic of public blockchains that cannot be prevented.

SACRIFICE MECHANICS

A "sacrifice" is a one-way, irreversible deposit of tokens to a deposit address generated for you, made across one of the supported blockchains as part of a token-distribution campaign. In exchange, the system may credit you with points according to a published time-decay schedule, where earlier sacrifices generally earn a better rate. Volume-based and other bonuses may apply during the early phases of the campaign as described in the published rules.

Points are a campaign accounting unit only. They confer NO ownership, NO equity, NO debt claim, NO guarantee of value, and NO guarantee of any future token distribution, amount, timing, or eligibility. The campaign rules, schedule, and parameters may change. Sacrificed funds are never returned regardless of how many points you receive or whether any distribution ever occurs.

CRYPTOCURRENCY VOLATILITY

Cryptocurrencies are extremely volatile. Bitcoin has experienced multiple 80%+ crashes throughout its history. Most cryptocurrencies drop 85-95% from peak prices. Coins and tokens can go to $0 and stay there permanently. The tokens you sacrifice, and any tokens that may be distributed in connection with this campaign, may be highly volatile and may lose all value. Only sacrifice funds you can afford to lose entirely. This is speculation, not investment.

INFRASTRUCTURE AND PRIVACY

This application is served through Cloudflare's infrastructure. Your Internet Protocol (IP) address, connection metadata, and usage patterns may be observed by Cloudflare and other network infrastructure. All blockchain transactions are permanently public and can be linked to your identity. For privacy considerations and security recommendations, see our Privacy Policy.

When you sign a message to access your account data, that signature cannot be revoked and may be intercepted or stolen. Only sign on trusted devices. See our Privacy Policy for detailed security guidance.

NO WARRANTIES OR LIABILITY

Sacrifice is provided "as is" without warranties of any kind. We disclaim all warranties of merchantability, fitness for a particular purpose, and non-infringement. To the maximum extent permitted by applicable law, our total liability is $0. We are not liable for any losses, damages, or claims arising from your use of this platform, including but not limited to: user errors, sacrifices sent to wrong or unsupported addresses, sacrifices of unsupported tokens or on unsupported networks, software bugs, network failures, smart contract vulnerabilities, indexing or pricing failures, incorrect point calculations, market volatility, or third-party actions.

TAX AND LEGAL COMPLIANCE

You are solely responsible for determining and paying all applicable taxes on your cryptocurrency transactions, including any sacrifice and any distribution you may receive. You are responsible for complying with all applicable laws in your jurisdiction, including but not limited to securities laws, money transmission regulations, and sanctions requirements. We do not provide legal or tax advice.

REGULATORY STATUS

Sacrifice is not registered with, licensed by, or regulated by the United States Securities and Exchange Commission (SEC), the Financial Industry Regulatory Authority (FINRA), the Commodity Futures Trading Commission (CFTC), or any other financial regulatory authority. We do not provide brokerage services, investment advice, or securities execution. Nothing on this platform constitutes an offer or solicitation to buy or sell any security, nor a promise of any future token, profit, or return.

The tokens you sacrifice, and any tokens that may be distributed in connection with this campaign, may be volatile and may be deemed securities or other regulated instruments in some jurisdictions. The regulatory landscape for digital assets is evolving and varies by jurisdiction. Nothing on this platform is investment advice. You are solely responsible for determining the legal and regulatory treatment of your participation in your own jurisdiction, and for any consequences thereof. This is a good-faith summary as of the date above and may not reflect future regulatory action or legislative changes.

THE HOWEY TEST

The Howey test (https://en.wikipedia.org/wiki/SEC_v._W._J._Howey_Co.) defines what United States federal securities laws apply to, which the Securities and Exchange Commission administers. Wherever possible you agree there is no investment of money. Wherever possible you agree there is no common enterprise. Wherever possible you agree there is no expectation of profit from the efforts of a promoter or any third party. You must have no expectation of profit from the work of others.

There is no common enterprise here. Users generate their own keys; no one else has keys to give them. The points credited to you are a database accounting value. Bonuses do not actually take anyone else's database values — they simply add or subtract more or less database value based on the published rules and the state of the system. A sacrifice is a one-way deposit you make to prove a belief, not a contribution to a pooled venture from which you expect a return generated by others.

CYBERSECURITY

This platform interacts with public blockchains and generates deposit addresses for your sacrifices. Standard web security risks apply, including but not limited to: browser extension exploits, cross-site scripting (XSS) attacks, compromised devices, and network interception. Sacrifice uses Hypertext Transfer Protocol Secure (HTTPS) in transit and does not custody your funds or store your private keys. Any smart contracts involved are immutable once deployed — no security patch can be applied to a deployed contract. See the Infrastructure and Privacy section above for detailed recommendations.

EVERY BENEFIT TO US, EVERY DISADVANTAGE TO YOU

If you have read this far, congratulations. The theme of everything above is that you should have absolutely no expectations of any sort regarding anything, and that if anything goes wrong, you should not look for redress anywhere and you should receive none. Everything is subject to change at any time. Software is hard. Blockchain software is harder. Everything could go terribly wrong and you would be out of luck. We are lucky any of this works at all.

Understand this disclaimer to give every single possible benefit to us, and every single possible disadvantage to you. It exists entirely to educate you and to protect us. Every single thing you read here, or anywhere else on the site, should be read to our benefit and not yours. If you can imagine more harsh language that would get you to take these disclaimers seriously, imagine it now. You are sacrificing your money with no expectation of the profit of others, and no expectations of any other sort.

If we ever end up in court, judicial notice will be given of this very strict, very elaborate, and very honest disclaimer, in which we did our absolute best to warn you and educate you.

By using this platform, you confirm that you have read and understood all risks described above. You proceed voluntarily and entirely at your own risk. These disclosures are provided in good faith to ensure you have the information necessary to make an informed decision.

---`

export const TERMS_OF_USE = `Last Updated: June 6th, 2026

These Terms of Use ("Terms") govern your access to and use of the Sacrifice platform ("Sacrifice", "the Platform", "we", "us", or "our"). By accessing or using Sacrifice, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access the Platform.

1. ACCEPTANCE AND ELIGIBILITY

By using Sacrifice, you represent and warrant that: (a) you are at least 18 years old and have the legal capacity to enter into these Terms; (b) you are not located in, organized in, or a resident of any jurisdiction where your use of Sacrifice would be illegal or restricted; (c) you will comply with all applicable laws in your jurisdiction; and (d) you have read and understood the Disclaimer and Privacy Policy.

2. NATURE OF THE PLATFORM

Sacrifice is a permissionless, multi-blockchain token-distribution campaign. You "sacrifice" — that is, irreversibly deposit — tokens to deposit addresses generated for you across the supported blockchains. The system observes those deposits on public blockchains, estimates their United States dollar value using third-party price sources, and credits you with points according to a published time-decay schedule. We do not custody funds on your behalf in any way that allows their return, do not act as a counterparty, and do not operate any escrow. A sacrifice is one-way and final: deposited funds are NOT returned.

Points are a campaign accounting unit only. They confer no ownership, equity, debt claim, guarantee of value, or guarantee of any future token distribution, amount, timing, or eligibility.

3. NO KNOW YOUR CUSTOMER, ANTI-MONEY-LAUNDERING, OR SANCTIONS SCREENING

Sacrifice does not perform Know Your Customer (KYC), Anti-Money Laundering (AML), or sanctions screening. We do not have the ability to block specific users or reverse transactions on public blockchains. You are solely responsible for complying with all applicable laws, including sanctions laws, money transmission regulations, and financial crime prevention requirements.

4. PROHIBITED USES

You may not use Sacrifice to: (a) engage in market manipulation or other deceptive practices; (b) intentionally exploit software bugs, indexing errors, pricing errors, or protocol vulnerabilities to inflate points or otherwise gain an unfair advantage; (c) launch denial-of-service attacks or spam the interface; (d) impersonate others or engage in fraud; (e) violate any applicable laws or regulations; (f) infringe on intellectual property rights; or (g) interfere with other users' access to the Platform.

We reserve the right to block access to the user interface (but cannot block blockchain interactions) if we become aware of violations. We further reserve the right to disregard, adjust, or recalculate points associated with prohibited activity.

5. WALLET-BASED ACCESS

Access to account data is controlled through cryptographic signatures from your wallet, using Ethereum typed structured data signatures (EIP-712). When you sign a message to prove ownership of a wallet address, you authorize access to data associated with that address. Signatures cannot be revoked once created. You are responsible for protecting your private keys and signing only on trusted devices. See our Privacy Policy for signature security details.

6. INTELLECTUAL PROPERTY

The Sacrifice user interface, including all text, graphics, logos, and software, is protected by copyright and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.

Smart contract code on public blockchains may be licensed under open-source licenses (such as MIT or GPL). Check individual contract repositories for specific license terms.

7. SERVICE MODIFICATIONS AND AVAILABILITY

We reserve the right to modify, suspend, or discontinue the user interface, the campaign, the points schedule, and any campaign parameters at any time without notice. We do not guarantee continuous, uninterrupted, or secure access. Sacrifices already made are irreversible regardless of any change to the Platform or campaign.

8. DISCLAIMERS

IN ADDITION TO THE DISCLAIMERS IN OUR DISCLAIMER DOCUMENT:

Sacrifice does not guarantee that any sacrifice will be detected, priced, or credited with points. You participate at your own risk.

We do not guarantee the accuracy of data displayed in the user interface, including but not limited to sacrifice detection, United States dollar valuations, point totals, decay-rate displays, bonus calculations, or leaderboard rankings. All such figures are best-effort estimates and may be wrong or revised.

9. LIMITATION OF LIABILITY

TO THE MAXIMUM EXTENT PERMITTED BY LAW:

Sacrifice's total liability to you for all claims arising from or related to your use of the Platform is $0 (zero dollars).

We are not liable for any direct, indirect, incidental, consequential, or punitive damages, including but not limited to: loss of funds, loss of profits, loss of data, service interruptions, smart contract vulnerabilities, indexing or pricing failures, incorrect point calculations, the non-occurrence of any token distribution, market volatility, or third-party actions.

Some jurisdictions do not allow the exclusion of certain warranties or limitation of liability for incidental or consequential damages. In such jurisdictions, our liability is limited to the maximum extent permitted by law.

10. INDEMNIFICATION

You agree to indemnify, defend, and hold harmless Sacrifice and its affiliates, officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising from: (a) your use of the Platform; (b) your violation of these Terms; (c) your violation of any law or regulation; (d) your violation of any third-party rights; or (e) your cryptocurrency transactions.

11. DISPUTE RESOLUTION

Any dispute arising from or relating to these Terms or your use of Sacrifice shall be resolved through binding arbitration in accordance with the Commercial Arbitration Rules of the American Arbitration Association. The arbitration shall be conducted in Pennsylvania, United States. Each party shall bear its own costs of arbitration.

You waive any right to participate in class actions or representative proceedings. You have 30 days from first use of Sacrifice to opt out of this arbitration agreement via Simplex private messaging: https://smp12.simplex.im/a#RWJ91caaRQktKypxxZUWlBui8C5ovkUPXWABmyulPD0

Disputes related to intellectual property rights (copyrights, trademarks, trade secrets, or patents) may be brought in court rather than arbitration.

12. GOVERNING LAW

These Terms shall be governed by and construed in accordance with the laws of the Commonwealth of Pennsylvania, United States, without regard to its conflict of law provisions. However, you agree that some claims may be subject to the laws of your local jurisdiction.

13. SEVERABILITY

If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect. The invalid or unenforceable provision shall be modified to the minimum extent necessary to make it valid and enforceable.

14. ENTIRE AGREEMENT

These Terms, together with our Disclaimer and Privacy Policy, constitute the entire agreement between you and Sacrifice regarding your use of the Platform. These Terms supersede all prior agreements, communications, and understandings, whether written or oral.

15. MODIFICATIONS

We may revise these Terms at any time without prior notice. Continued use of Sacrifice after changes constitutes acceptance of the revised Terms. We will update the "Last Updated" date at the top of this document when changes are made.

16. NO WAIVER

Our failure to enforce any right or provision of these Terms shall not be deemed a waiver of such right or provision. No waiver by us of any term or condition shall be deemed a continuing waiver of such term or any other term.

17. CONTACT

For questions about these Terms, contact us via Simplex private messaging: https://smp12.simplex.im/a#RWJ91caaRQktKypxxZUWlBui8C5ovkUPXWABmyulPD0

You acknowledge that you have read and understood these Terms, our Disclaimer, and our Privacy Policy, and agree to be bound by them.

---`

export const PRIVACY_POLICY = `Last Updated: June 6th, 2026

This Privacy Policy explains how Sacrifice ("we", "us", or "our") handles data when you use the Platform. If you really care about your privacy, you should be using the Tor Browser (https://www.torproject.org). We prefer not to have any data about you at all, really — it is just a liability. We prioritize user privacy and aim to collect as little data as possible. However, infrastructure providers we rely on may collect data as part of their operations. No matter what any company writes in its terms of service, it can always change them or break its own rules, so you must be responsible for protecting your own privacy.

1. DATA WE COLLECT

Sacrifice does NOT directly collect, store, or process personal information. We do not use cookies for tracking or third-party advertising services.

However, third-party infrastructure providers collect data as described below.

2. USE OF INFORMATION

The information collected by our infrastructure providers is used for the following purposes: (a) operating, maintaining, and improving the platform; (b) providing technical support and responding to inquiries; (c) generating aggregated and anonymized analytics to understand usage patterns; (d) detecting, preventing, and addressing fraud, abuse, and security threats; and (e) complying with applicable legal obligations. We do not sell your personal information or use it for advertising.

3. CLOUDFLARE INFRASTRUCTURE

This application is served through Cloudflare's global Content Delivery Network (CDN). Cloudflare acts as a reverse proxy between your browser and our servers. Cloudflare may collect:

- Internet Protocol (IP) addresses
- Connection timestamps and duration
- Hypertext Transfer Protocol (HTTP) request headers (User-Agent, Referer, etc.)
- Request Uniform Resource Locators (URLs) and methods
- Geographic location (derived from IP address)
- Browser and device information

Cloudflare uses this data for: distributed-denial-of-service protection, bot management, performance optimization, security threat detection, and aggregate analytics.

Cloudflare's data practices are governed by their privacy policy: https://www.cloudflare.com/privacypolicy/

We have no control over what information Cloudflare collects, processes, or retains. By using Sacrifice, you acknowledge and accept Cloudflare's role and data practices.

4. BLOCKCHAIN DATA TRANSPARENCY

All blockchain transactions are permanently public and immutable. Anyone can view your:

- Wallet and deposit addresses
- Sacrifice amounts and timestamps
- Smart contract interactions
- Token holdings and transfer history
- On-chain activity patterns

Privacy Implications:

Address Linkability: Your wallet address can be linked to your real identity through: Know Your Customer (KYC) data from centralized exchanges, IP address logging during transactions, browser fingerprinting, off-chain information you share, transaction pattern analysis, or public blockchain explorer services.

Permanent Records: Blockchain data cannot be deleted, modified, or hidden. Once a transaction is confirmed, it is public forever. A sacrifice, once made, is a permanent public record.

Transaction Graphs: Sophisticated observers can analyze transaction flows to identify relationships between addresses, infer wallet ownership, and track fund movements across multiple addresses and across the many blockchains supported by this campaign.

5. WALLET PROVIDERS AND EXTENSIONS

When you connect a wallet (such as MetaMask, WalletConnect, or Coinbase Wallet), these providers may collect:

- Your IP address
- Wallet addresses you control
- Transaction requests you initiate
- Decentralized-application interactions

We do not control wallet provider data collection. Review their respective privacy policies.

6. SIGNATURE-BASED AUTHORIZATION

Sacrifice uses cryptographic signatures (Ethereum typed structured data signatures, EIP-712) to authorize access to your account data. When you sign a message to prove wallet ownership, you create authorization credentials that:

CANNOT BE REVOKED: Once a signature exists, there is no way to invalidate it before it expires (the maximum signature age is one hour, enforced by the server).

CAN BE STOLEN: Signatures may be intercepted through: insecure network connections, malicious browser extensions, cross-site scripting (XSS) attacks, compromised devices, server-side logging, or stolen browser storage data.

CAN BE REPLAYED: The same signature can be reused multiple times until the timestamp expires, potentially by unauthorized parties.

GRANT ACCESS: A signature authorizes access to data associated with your wallet address, including your sacrifice history and account details.

What Happens to Your Signatures:

The server receives every signature you send, even over Hypertext Transfer Protocol Secure (HTTPS). Signatures may be logged in server logs, database records, backup systems, and error logs. We cannot guarantee these logs are encrypted or deleted. Hosting providers, content-delivery-network operators, and system administrators may have access to signature logs.

7. THIRD-PARTY DATA FLOWS

Blockchain Indexers and Data Sources: Sacrifice relies on third-party blockchain indexing and explorer services (for example, block explorers and node-provider application programming interfaces) to detect and display your sacrifices across the supported blockchains. These services independently read public blockchain data and may log requests we make on the campaign's behalf.

Price Sources: Sacrifice estimates United States dollar valuations using third-party price sources, including centralized exchanges and on-chain automated market makers. These sources independently provide market data; we do not transmit your personal information to them for this purpose.

Simplex Messaging: If you use the Simplex private messaging link for support, Simplex operates an independent encrypted messaging service. Review their privacy policy at https://simplex.chat/privacy

8. PRIVACY RECOMMENDATIONS

We strongly recommend the following practices to protect your privacy:

USE TOR BROWSER

Download from https://www.torproject.org. The Tor network routes your traffic through multiple relays, hiding your IP address from Cloudflare and other observers. This is the strongest privacy protection available.

Verify Tor is working: Visit https://check.torproject.org before using Sacrifice.

Limitations: Tor is slower than direct connections. Tor exit nodes can observe unencrypted traffic (though Sacrifice uses HTTPS). Sophisticated attackers may still de-anonymize Tor users through timing analysis or network traffic correlation.

USE A VIRTUAL PRIVATE NETWORK

A Virtual Private Network (VPN) hides your IP address from Cloudflare and internet service providers. Choose a privacy-focused VPN provider that: does not log connection data, is located in a privacy-friendly jurisdiction, accepts anonymous payment (such as Bitcoin), and has been independently audited.

Limitations: VPNs can see your traffic and may log despite claims otherwise. You are trusting the VPN provider. Free VPNs often sell user data.

USE HARDWARE WALLETS

Never expose your private keys to websites. Use hardware wallets (such as Ledger or Trezor) to sign transactions. Hardware wallets keep your keys isolated from potentially compromised computers.

USE SECURE BROWSERS

Firefox or Brave offer better privacy than Chrome. Use private/incognito mode to avoid persistent cookies. Enable "Resist Fingerprinting" in Firefox advanced settings.

DISABLE BROWSER EXTENSIONS

Extensions can read page content, steal signatures from browser storage, and intercept transactions. Disable all extensions or use a clean browser profile for cryptocurrency transactions.

CLEAR BROWSER STORAGE REGULARLY

Clear cookies and browser storage after each session to remove stored signatures and session data. In browser developer tools, go to Application > Storage > Clear site data.

USE SEPARATE WALLETS

Create separate wallets for testing small amounts versus holding significant funds. This compartmentalizes risk and limits exposure if a wallet is compromised. Note that, because the leaderboard and points are tied to the wallet you sacrifice from, using multiple addresses for privacy will split your points across those addresses.

ONLY SIGN ON TRUSTED DEVICES

Never sign messages on: public computers, internet cafes, shared workstations, work computers, devices with unknown software, or devices connected to public Wi-Fi without a VPN.

UNDERSTAND BROWSER FINGERPRINTING

Even without cookies, browsers can be uniquely identified through: canvas fingerprinting, WebGL fingerprinting, font enumeration, screen resolution and color depth, timezone and language settings, and installed plugins.

Tor Browser provides the best protection against fingerprinting by making all users look identical.

These recommendations are not requirements. We make no guarantees about the effectiveness of any privacy tool or practice. Some techniques may conflict (for example, a VPN combined with Tor can reduce anonymity in some cases). You are solely responsible for protecting your own privacy and security.

9. DATA RETENTION AND LOGS

Server Logs: We may keep server logs for debugging, security monitoring, and performance optimization. We do not have a fixed retention policy — logs may be kept indefinitely. We cannot guarantee logs are encrypted or access-controlled.

Cloudflare Logs: Cloudflare retains logs according to their own policies, which may vary by service and data type. We do not control Cloudflare's retention practices.

Blockchain Data: Blockchain data is permanent and cannot be deleted by anyone, including us.

10. DATA SECURITY

We implement reasonable technical and organizational security measures to protect our systems and the data transmitted to and from our platform. These include HTTPS encryption in transit, access controls on infrastructure, and security monitoring. However, no security system is impenetrable. We cannot guarantee the absolute security of any data you transmit to or from our platform. You use Sacrifice entirely at your own risk.

11. INTERNATIONAL USERS AND DATA TRANSFERS

Cloudflare operates globally. Your data may be transferred to, stored in, and processed in countries other than your own, including the United States. These countries may have different data protection laws than your jurisdiction.

EUROPEAN UNION USERS (GENERAL DATA PROTECTION REGULATION):

You have certain rights under the General Data Protection Regulation (GDPR):
- Right to access your data
- Right to rectification (correction)
- Right to erasure ("right to be forgotten")
- Right to restrict processing
- Right to data portability
- Right to object to processing

Limitations for blockchain data: We cannot delete, modify, or restrict access to blockchain data, as it is permanently public and immutable. We can only address data we directly control (if any).

To exercise your rights, contact us via Simplex private messaging: https://smp12.simplex.im/a#RWJ91caaRQktKypxxZUWlBui8C5ovkUPXWABmyulPD0

CALIFORNIA USERS (CALIFORNIA CONSUMER PRIVACY ACT):

You have certain rights under the California Consumer Privacy Act (CCPA):
- Right to know what personal information is collected
- Right to know if personal information is sold or shared
- Right to opt-out of sale/sharing
- Right to deletion
- Right to non-discrimination

We do not sell your personal information. We share data with Cloudflare for infrastructure purposes (not considered a "sale" under the CCPA).

To exercise your rights, contact us via Simplex private messaging: https://smp12.simplex.im/a#RWJ91caaRQktKypxxZUWlBui8C5ovkUPXWABmyulPD0

12. CHILDREN'S PRIVACY

Sacrifice does not knowingly collect information from children under 18 years of age. If you are under 18, you may not use Sacrifice. If you believe a child under 18 has provided information to us, contact us immediately via Simplex private messaging (https://smp12.simplex.im/a#RWJ91caaRQktKypxxZUWlBui8C5ovkUPXWABmyulPD0) and we will make reasonable efforts to address such information.

13. DO NOT TRACK

Sacrifice does not respond to "Do Not Track" (DNT) browser signals. However, we do not use tracking cookies or third-party analytics that would be affected by DNT settings. Cloudflare may or may not honor DNT signals — see their privacy policy.

14. THIRD-PARTY LINKS

Sacrifice may contain links to third-party websites, services, or resources. This Privacy Policy does not apply to those external sites. We are not responsible for the privacy practices, content, or policies of any third-party websites. We encourage you to review the privacy policies of any third-party sites or services you visit or use in connection with Sacrifice.

15. CHANGES TO THIS POLICY

We may update this Privacy Policy at any time without notice. Changes will be effective when posted. The "Last Updated" date at the top reflects the most recent revision. Continued use of Sacrifice after changes constitutes acceptance of the updated policy.

You must take responsibility for your own privacy. Use the privacy tools and practices recommended above, and assume all data you expose is potentially public or logged.

16. CONTACT INFORMATION

For privacy inquiries, to exercise your rights, or to report privacy concerns:

Simplex: https://smp12.simplex.im/a#RWJ91caaRQktKypxxZUWlBui8C5ovkUPXWABmyulPD0

Please allow up to 30 days for responses to privacy requests. For GDPR and CCPA requests, we will respond within the legally required timeframes (typically 30-45 days).

17. CONSENT

By using Sacrifice, you consent to this Privacy Policy and acknowledge that: (a) your data may be collected by Cloudflare and other third parties; (b) blockchain data is permanently public; (c) signatures cannot be revoked and may be logged; (d) you are responsible for your own privacy protection; and (e) we make no guarantees about the privacy or security of any data related to your use of Sacrifice.

---`

/**
 * Short risk bullets shown as a lead-in on the Disclaimer tab of the
 * Agreements modal. Display-only — NOT part of any section's hashed text, so
 * editing a bullet does not by itself re-prompt acceptance. Keep the bullets in
 * sync with the full Disclaimer text if a change is material.
 */
export const RISK_BULLETS: readonly string[] = [
  'Sacrifices are one-way and permanent. There is no undo and no refund — funds you sacrifice are never returned.',
  'This is cutting-edge software. Any layer of the system could have a bug, and the displayed United States dollar values and points may be wrong.',
  'Points guarantee nothing — no value, and no promise of any future token distribution. Only sacrifice what you can afford to lose entirely.',
]

/**
 * Strip a trailing markdown horizontal rule from a section's text for display.
 *
 * The legal docs are generated from markdown that uses "---" as a section
 * separator, leaving a dangling rule at the very end of each document. Rendered
 * in a tab that shows one document on its own, that trailing "---" reads as a
 * stray line of dashes. We remove it for display ONLY — the canonical `text`
 * (and therefore the consent hash) is left untouched, so this does not
 * re-prompt anyone to re-accept.
 */
export const stripTrailingRule = (text: string): string =>
  text.replace(/\n*\s*-{3,}\s*$/, '').trimEnd()

/** The three global (cross-campaign) consent sections. */
export type GlobalConsentSectionKey = 'disclaimer' | 'terms' | 'privacy'

export interface ConsentSection {
  key: GlobalConsentSectionKey
  /** Tab label. */
  label: string
  /** Canonical text used for hashing — never alter without intending to re-prompt. */
  text: string
  /** Cleaned-up text for display (trailing separator rule removed). */
  displayText: string
}

export const CONSENT_SECTIONS: readonly ConsentSection[] = [
  {
    key: 'disclaimer',
    label: 'Disclaimer',
    text: DISCLAIMER_TEXT,
    displayText: stripTrailingRule(DISCLAIMER_TEXT),
  },
  {
    key: 'terms',
    label: 'Terms',
    text: TERMS_OF_USE,
    displayText: stripTrailingRule(TERMS_OF_USE),
  },
  {
    key: 'privacy',
    label: 'Privacy',
    text: PRIVACY_POLICY,
    displayText: stripTrailingRule(PRIVACY_POLICY),
  },
]

/** localStorage key for the per-section consent state (shape: ConsentState). */
export const CONSENT_STORAGE_KEY = 'sacrifice_consent_v1'

/**
 * Current content hash of each global section. Computed once at module load —
 * the policy strings are constants, so the hashes are too.
 */
export const SECTION_HASHES: Record<GlobalConsentSectionKey, string> = Object.fromEntries(
  CONSENT_SECTIONS.map(s => [s.key, hashString(s.text)])
) as Record<GlobalConsentSectionKey, string>
